import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationProps {
    page: number;
    totalPages: number;
    total: number;
    limit: number;
    onPageChange: (page: number) => void;
    onLimitChange: (limit: number) => void;
}

export default function Pagination({
    page,
    totalPages,
    total,
    limit,
    onPageChange,
    onLimitChange,
}: PaginationProps) {
    if (total === 0) return null;

    const start = (page - 1) * limit + 1;
    const end = Math.min(page * limit, total);

    const getPageNumbers = (): (number | "...")[] => {
        const pages: (number | "...")[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (page > 3) pages.push("...");
            const s = Math.max(2, page - 1);
            const e = Math.min(totalPages - 1, page + 1);
            for (let i = s; i <= e; i++) pages.push(i);
            if (page < totalPages - 2) pages.push("...");
            pages.push(totalPages);
        }
        return pages;
    };

    return (
        <div className="kb-pagination">
            <div className="kb-pagination-info">
                Showing {start}–{end} of {total}
                <span className="kb-page-size">
                    <select
                        value={limit}
                        onChange={(e) => onLimitChange(Number(e.target.value))}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                    per page
                </span>
            </div>

            {totalPages > 1 && (
                <div className="kb-pagination-controls">
                    <button
                        className="kb-page-btn"
                        onClick={() => onPageChange(page - 1)}
                        disabled={page <= 1}
                    >
                        <ChevronLeft size={16} />
                    </button>

                    {getPageNumbers().map((p, idx) =>
                        p === "..." ? (
                            <span key={`ellipsis-${idx}`} className="kb-page-ellipsis">...</span>
                        ) : (
                            <button
                                key={p}
                                className={`kb-page-btn ${p === page ? "active" : ""}`}
                                onClick={() => onPageChange(p)}
                            >
                                {p}
                            </button>
                        )
                    )}

                    <button
                        className="kb-page-btn"
                        onClick={() => onPageChange(page + 1)}
                        disabled={page >= totalPages}
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            )}
        </div>
    );
}
